import React from "react";
import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import { Accordion as RadixAccordion } from "radix-ui";
import { ChevronDown } from "lucide-react";

export interface AccordionItemProps {
  value: string;
  showMore: React.ReactNode;
  showLess?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
  triggerProps?: RadixAccordion.AccordionTriggerProps;
  triggerPosition?: "top" | "bottom";
  contentProps?: RadixAccordion.AccordionContentProps;
}

const AccordionItem = ({
  value,
  showMore,
  showLess,
  children,
  className,
  triggerProps,
  triggerPosition = "top",
  contentProps,
}: AccordionItemProps) => {
  const triggerElement = (
    <RadixAccordion.Header>
      <RadixAccordion.Trigger
        {...triggerProps}
        className={twMerge(
          clsx(
            "group flex w-full cursor-pointer items-center gap-2 py-3 text-sm font-medium",
            "text-gray-900 transition-colors hover:text-gray-900/60 focus:outline-none focus-visible:ring-2",
            "focus-visible:ring-offset-2",
            triggerProps?.className,
          ),
        )}
      >
        <ChevronDown
          size={20}
          className="shrink-0 transition-transform duration-200 group-data-[state=open]:rotate-180"
        />
        <span className={clsx(showLess && "group-data-[state=open]:hidden")}>
          {showMore}
        </span>
        {showLess && (
          <span className="group-data-[state=closed]:hidden">{showLess}</span>
        )}
      </RadixAccordion.Trigger>
    </RadixAccordion.Header>
  );
  const contentElement = (
    <RadixAccordion.Content
      {...contentProps}
      className={twMerge(
        clsx(
          "data-[state=closed]:animate-radixAccordionClose overflow-hidden py-3 text-sm",
          "data-[state=open]:animate-radixAccordionOpen",
          contentProps?.className,
        ),
      )}
    >
      {children}
    </RadixAccordion.Content>
  );
  return (
    <RadixAccordion.Item
      value={value}
      className={twMerge(clsx("border-gray-200", className))}
    >
      {triggerPosition === "top" ? (
        <>
          {triggerElement}
          {contentElement}
        </>
      ) : (
        <>
          {contentElement}
          {triggerElement}
        </>
      )}
    </RadixAccordion.Item>
  );
};

export interface AccordionSingleProps {
  className?: string;
  children: React.ReactNode;
  type?: "single";
  collapsible?: boolean;
  rootProps?: Partial<RadixAccordion.AccordionSingleProps>;
}

export interface AccordionMultipleProps {
  className?: string;
  children: React.ReactNode;
  type: "multiple";
  rootProps?: Partial<RadixAccordion.AccordionMultipleProps>;
}

export type AccordionProps = AccordionSingleProps | AccordionMultipleProps;
export function Accordion(props: AccordionProps) {
  const { className, children, rootProps } = props;

  if (props.type === "single") {
    return (
      <RadixAccordion.Root
        type="single"
        {...(rootProps as Partial<RadixAccordion.AccordionSingleProps>)}
        collapsible={props.collapsible ?? true}
        className={twMerge(clsx("divide-y", className))}
      >
        {children}
      </RadixAccordion.Root>
    );
  }

  return (
    <RadixAccordion.Root
      type="multiple"
      {...(rootProps as Partial<RadixAccordion.AccordionMultipleProps>)}
      className={twMerge(clsx("divide-y", className))}
    >
      {children}
    </RadixAccordion.Root>
  );
}

Accordion.Item = AccordionItem;
